import { toast } from "sonner";
import { AppToast, type AppToastVariant } from "../components/blocks/app-toast";
import { wait } from "./helpers";
import type { ToastOptions } from "../types/toast";

const toToastVariant = (
  toastType: ToastOptions["toastType"]
): AppToastVariant => {
  if (toastType === "information" || toastType === "info") return "info";
  if (
    toastType === "success" ||
    toastType === "error" ||
    toastType === "warning"
  ) {
    return toastType;
  }
  return "default";
};

// Module-level dedup guard; the old provider was a single app-wide instance.
let lastNotification: ToastOptions | null = null;

export const addNotification = (newNotification: ToastOptions): void => {
  if (
    lastNotification &&
    lastNotification.heading === newNotification.heading
  ) {
    return;
  }

  lastNotification = newNotification;

  let defaultTime = 3000;

  const messageLength =
    typeof newNotification.heading === "string"
      ? newNotification.heading.length
      : String(newNotification.heading ?? "").length;

  if (messageLength > 100 && messageLength <= 150) {
    defaultTime = 4000;
  } else if (messageLength > 150) {
    defaultTime = 5000;
  }

  const autoClose = newNotification.autoClose ?? defaultTime;

  const duration = autoClose === false ? Infinity : autoClose;

  // All app toasts render through the shared themed AppToast surface so the
  // whole app matches (quiet card + tinted icon badge). Variant drives the
  // icon + accent; the message is whatever the caller passed as `heading`.
  toast.custom(
    () => (
      <AppToast
        variant={toToastVariant(newNotification.toastType)}
        message={newNotification.heading}
        action={newNotification.action}
      />
    ),
    { duration }
  );

  // A persistent toast (autoClose: false) never auto-dismisses, so there's
  // no duration to clear the dedup guard after — leave it set until a
  // different heading comes through, rather than lifting it almost
  // immediately via wait(0).
  if (autoClose !== false) {
    wait(autoClose).then(() => {
      lastNotification = null;
    });
  }
};

export const notification = (props: ToastOptions): void => {
  const toastNotification = new CustomEvent<ToastOptions>("toastNotification", {
    detail: props
  });
  window.dispatchEvent(toastNotification);
};
